import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type RowDoubleClickedEvent, type CellStyle } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, Input, MenuItem, Select, Tooltip } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./popup_inbound_info.scoped.scss";

interface State {
  gridApi: GridApi | null
}

const defState: State = {
  gridApi: null
};

interface SearchParams {
  wh: string,
  trackingIdLike: string,
  pnLike: string,
  mngType: string
}

const defSearchParams: SearchParams = {
  wh: "",
  trackingIdLike: "",
  pnLike: "",
  mngType: ""
};

interface Result {
  searchParams: SearchParams,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  infos: []
};

const validationMsg = [
  {id: "workReqNo", type: "DupReq", msg: "이미 작업지시한 작업입니다."},
  {id: "skuNo", type: "NotFound", msg: "SKU가 없습니다."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."},
  {id: "skuCellQty", type: "InvalidStatus", msg: "적재 가능 SKU Cell이 없습니다."},
  {id: "stockRate", type: "InvalidStatus", msg: "적재 가능 SKU Cell을 초과하였습니다."},
  {id: "pn", type: "InvalidStatus", msg: "다른 창고의 동일한 P/N은 입고할 수 없습니다."},
  {id: "eaiId", type: "InvalidStatus", msg: (_p: any, e: any) => `${e.desc}`}
];

function PopupInboundInfo(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const gridStateKey = "/inoutbound/popup_inbound_info";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const tmpCellStyle1: CellStyle = {textAlign: "center"};
  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Creat Date", field: "createDate", width: 180, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "창고", field: "wh", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "Tracking ID", field: "trackingId", width: 120, cellStyle: tmpCellStyle1, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Request Qty", headerClass: "hd-ta-right", field: "reqQty", width: 130, cellStyle: {textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Complete Qty", headerClass: "hd-ta-right", field: "completeQty", width: 130, cellStyle: {textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Remain Qty", headerClass: "hd-ta-right", field: "remainQty", width: 130, cellStyle: {textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "관리구분", field: "mngType", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("mngType"), filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "singleRow" as const,
      checkboxes: false,
      enableClickSelection: true
    },
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    },
    onRowDoubleClicked: (_e: RowDoubleClickedEvent) => {
      orderWork();
    }
  });

  useEffect(() => {
    init();
  }, []);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!props.isShow) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [props.isShow]);
  // 끝: 바코드 스캔 기능 적용

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await doSearch();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
  }

  async function doSearch() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/inoutbound/workstation_manage.data?mode=searchWorkReq", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    props.onClose();
  }

  async function orderWork() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("작업을 선택해 주세요.");
      return;
    }

    try {
      const editMode = selectedInfos[0].workReqType == "ERP" ? "orderWorkErp" : "orderWorkEtc";
      selectedInfos[0].skuNo = props.data.info.skuNo;
      selectedInfos[0].workStationId = props.data.workStationId;
      selectedInfos[0].choiceSkuType = "S";
      selectedInfos[0].choiceSkuCallQty = "1";
      selectedInfos[0].choiceSkuStockQty = "1";
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=" + editMode, selectedInfos[0], {
        validationMsg: validationMsg
      });
      props.onClose({isSuccess: true});
    } catch(e) {
      console.log(e);
    }
  }

  function selectTrackingId(trackingId: string) {
    const api = state.gridApi;
    if (!api) return;

    let found = false;
    api.forEachNode((node) => {
      if (node.data?.trackingId === trackingId) {
        node.setSelected(true);
        api.ensureNodeVisible(node, "middle");
        found = true;
      }
    });

    if (!found) {
      kutil.alert("일치하는 Tracking ID가 없습니다.\r\n" + trackingId);
    } else {
      orderWork();
    }
  }

  async function onBarcodeScan(code: string) {
    selectTrackingId(code);
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{"& .MuiDialog-container": {"& .MuiPaper-root": {width: "100%", maxWidth: "1500px",},},}}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          입고 예정 정보
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* 검색 */}
            <div className="srch-area">
              <div>
                <p>창고</p>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <Select
                    value={searchParams.wh}
                    onChange={(e) => {
                      updateSearchParams({wh: e.target.value});
                      doSearch();
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">전체</MenuItem>
                    {(commonCode.wh as any[]).map(code => (
                      <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div>
                <p>Tracking ID</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.trackingIdLike}
                  onChange={(e) => {updateSearchParams({ trackingIdLike: e.target.value })}}
                  onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
                />
              </div>
              <div>
                <p>P/N</p>
                <Input
                  inputProps={{ maxLength: 100 }}
                  value={searchParams.pnLike}
                  onChange={(e) => {updateSearchParams({ pnLike: e.target.value })}}
                  onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
                />
              </div>
              <div>
                <p>관리구분</p>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <Select
                    value={searchParams.mngType}
                    onChange={(e) => {
                      updateSearchParams({ mngType: e.target.value });
                      doSearch();
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">전체</MenuItem>
                    {(commonCode.mngType as any[]).map((code) => (
                      <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <p>
                <Tooltip title="검색">
                  <button className="icon-button popup-search" onClick={doSearch}>검색</button>
                </Tooltip>
              </p>
            </div>
            {/*// 검색 */}
            {/* 그리드 */}
            <AgGridProvider modules={modules}>
              <div className="grid-area">
                <CustomAgGridReact
                  columnDefs={gridInfo}
                  rowData={result.infos}
                  defaultColDef={kutil.defaultColDef}
                  initialState={gridState}
                  rowSelection={gridOptions.rowSelection}
                  onGridReady={gridCallbacks.onGridReady}
                  onStateUpdated={gridCallbacks.onStateUpdated}
                  onRowDoubleClicked={gridCallbacks.onRowDoubleClicked}
                />
              </div>
            </AgGridProvider>
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            닫기
          </Button>
          { auth.write &&
          <Button variant="contained" autoFocus onClick={orderWork}>
            확인
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupInboundInfo);
